import type { MoleculeDrawingDocument } from '@peruselab/structure-editor/domain/moleculeDrawingProjection';
import { apiFetch, ApiNetworkError, tenantHeaders } from './apiClient';

// #147: the v11-native counterpart to drawings.ts, calling the #146 endpoint
// (/api/molecule-drawings) directly rather than /api/drawings's v10-shimmed one. Deliberately
// self-contained (own error class, not a shared import from drawings.ts) so drawings.ts can be
// deleted cleanly once #148 retires the shim, without this file needing to change.

export interface StoredMoleculeDrawing {
  readonly id: string;
  readonly documentRevision: number;
  readonly title: string | null;
  readonly schemaVersion: string;
  readonly document: MoleculeDrawingDocument;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly archivedAt: string | null;
  /** #811 -- 'private' (the default) or 'public'; `owned` is false for someone else's public one. */
  readonly visibility?: DrawingVisibility;
  readonly owned?: boolean;
  /** #885 -- the author's display name, shown to everyone; null until one has been recorded. */
  readonly authorName?: string | null;
}

export type DrawingVisibility = 'private' | 'public';

export interface MoleculeDrawingSummary {
  readonly id: string;
  readonly documentRevision: number;
  readonly title: string | null;
  readonly schemaVersion: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly archivedAt: string | null;
  /** #682 -- the molecule to draw as this drawing's thumbnail (its first isomer's), if any. */
  readonly thumbnailMoleculeId?: string | null;
  /** #886 Q6 -- live, unhidden comments on it. */
  readonly commentCount?: number;
}

export interface MoleculeDrawingPage {
  readonly content: readonly MoleculeDrawingSummary[];
  readonly page: number;
  readonly size: number;
  readonly totalElements: number;
  readonly totalPages: number;
}

/** #885 -- an entry in the public browse listing (anyone's live public drawings). */
export interface PublicMoleculeDrawingSummary {
  readonly id: string;
  readonly documentRevision: number;
  readonly title: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly thumbnailMoleculeId: string | null;
  readonly authorName: string | null;
}

export interface PublicMoleculeDrawingPage {
  readonly content: readonly PublicMoleculeDrawingSummary[];
  readonly page: number;
  readonly size: number;
  readonly totalElements: number;
  readonly totalPages: number;
}

export type MoleculeDrawingSort = 'title' | 'createdAt' | 'updatedAt';
export type SortDirection = 'asc' | 'desc';
export type MoleculeDrawingArchiveStatus = 'active' | 'archived' | 'all';

interface ApiProblem {
  readonly title?: unknown;
  readonly detail?: unknown;
  readonly currentRevision?: unknown;
}

export class MoleculeDrawingApiError extends Error {
  readonly status: number;
  readonly currentRevision: number | undefined;

  constructor(message: string, status: number, currentRevision?: number) {
    super(message);
    this.name = 'MoleculeDrawingApiError';
    this.status = status;
    this.currentRevision = currentRevision;
  }
}

async function parseResponse(response: Response): Promise<unknown> {
  const contentType = response.headers.get('content-type') ?? '';
  const body =
    contentType.includes('application/json') || contentType.includes('application/problem+json')
      ? await response.json()
      : await response.text();

  // Every successful drawing response is JSON. Anything else (e.g. the app's own HTML page from a
  // proxy that sent /api to the frontend) is an error, not data the page would then crash on.
  if (response.ok && typeof body !== 'string') return body;
  if (response.ok) {
    throw new MoleculeDrawingApiError('The server sent an unexpected response.', response.status);
  }

  const problem = body && typeof body === 'object' ? (body as ApiProblem) : undefined;
  const title = typeof problem?.title === 'string' ? problem.title : undefined;
  const detail = typeof problem?.detail === 'string' ? problem.detail : undefined;
  const currentRevision = typeof problem?.currentRevision === 'number' ? problem.currentRevision : undefined;
  throw new MoleculeDrawingApiError(
    detail ?? title ?? `Molecule drawing request failed with status ${response.status}.`,
    response.status,
    currentRevision
  );
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;
  try {
    response = await apiFetch(path, init);
  } catch (error) {
    throw new MoleculeDrawingApiError(
      error instanceof ApiNetworkError
        ? `Unable to reach the molecule drawing API: ${error.message}`
        : 'Unable to reach the molecule drawing API.',
      0
    );
  }
  if (response.status === 401) {
    throw new MoleculeDrawingApiError('Session expired. Redirecting to sign in…', 401);
  }
  return (await parseResponse(response)) as T;
}

export function getMoleculeDrawing(id: string): Promise<StoredMoleculeDrawing> {
  return request<StoredMoleculeDrawing>(`/molecule-drawings/${encodeURIComponent(id)}`);
}

export function listMoleculeDrawings(
  page = 0,
  size = 10,
  search = '',
  sort: MoleculeDrawingSort = 'updatedAt',
  direction: SortDirection = 'desc',
  status: MoleculeDrawingArchiveStatus = 'active'
): Promise<MoleculeDrawingPage> {
  const query = new URLSearchParams({
    page: String(page),
    size: String(size),
    sort,
    direction,
    status
  });
  if (search.trim()) query.set('query', search.trim());
  return request<MoleculeDrawingPage>(`/molecule-drawings?${query}`);
}

/** #885 -- browse anyone's public drawings; open to signed-out visitors. */
export function listPublicMoleculeDrawings(
  page = 0,
  size = 24,
  search = '',
  sort: MoleculeDrawingSort = 'updatedAt',
  direction: SortDirection = 'desc'
): Promise<PublicMoleculeDrawingPage> {
  const query = new URLSearchParams({ page: String(page), size: String(size), sort, direction });
  if (search.trim()) query.set('query', search.trim());
  return request<PublicMoleculeDrawingPage>(`/molecule-drawings/public?${query}`);
}

export function createMoleculeDrawing(
  document: MoleculeDrawingDocument,
  title: string
): Promise<StoredMoleculeDrawing> {
  const normalizedTitle = title.trim();
  const query = normalizedTitle ? `?${new URLSearchParams({ title: normalizedTitle })}` : '';
  return request<StoredMoleculeDrawing>(`/molecule-drawings${query}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(document)
  });
}

/** #1051: `tenant` pins the request to the drawing's own tenant (see tenantHeaders). */
export function updateMoleculeDrawing(
  id: string,
  documentRevision: number,
  document: MoleculeDrawingDocument,
  title: string,
  tenant?: string | null
): Promise<StoredMoleculeDrawing> {
  return request<StoredMoleculeDrawing>(`/molecule-drawings/${encodeURIComponent(id)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...tenantHeaders(tenant) },
    body: JSON.stringify({ documentRevision, document, title })
  });
}

function setMoleculeDrawingArchived(
  id: string,
  documentRevision: number,
  action: 'archive' | 'restore',
  tenant?: string | null
): Promise<StoredMoleculeDrawing> {
  return request<StoredMoleculeDrawing>(`/molecule-drawings/${encodeURIComponent(id)}/${action}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...tenantHeaders(tenant) },
    body: JSON.stringify({ documentRevision })
  });
}

/** #811 -- the owner makes a drawing public or private (the document revision is unchanged). */
export function setMoleculeDrawingVisibility(
  id: string,
  visibility: DrawingVisibility,
  tenant?: string | null
): Promise<StoredMoleculeDrawing> {
  return request<StoredMoleculeDrawing>(`/molecule-drawings/${encodeURIComponent(id)}/visibility`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...tenantHeaders(tenant) },
    body: JSON.stringify({ visibility })
  });
}

/** #1060 (D13): the drawing's molecules still private to its tenant -- the publish prompt lists them. */
export function listPrivateMolecules(id: string, tenant?: string | null): Promise<readonly string[]> {
  return request<readonly string[]>(`/molecule-drawings/${encodeURIComponent(id)}/private-molecules`, {
    headers: tenantHeaders(tenant)
  });
}

export function archiveMoleculeDrawing(
  id: string,
  documentRevision: number,
  tenant?: string | null
): Promise<StoredMoleculeDrawing> {
  return setMoleculeDrawingArchived(id, documentRevision, 'archive', tenant);
}

export function restoreMoleculeDrawing(
  id: string,
  documentRevision: number,
  tenant?: string | null
): Promise<StoredMoleculeDrawing> {
  return setMoleculeDrawingArchived(id, documentRevision, 'restore', tenant);
}
